import { useState, useEffect, useCallback } from 'react';
import {
  X, ChevronRight, ChevronLeft, ChevronDown, AlertCircle, Clock, Shield,
  Video, Phone, Search as SearchIcon, Loader, Check, Calendar,
  DoorOpen, Activity
} from 'lucide-react';
import { toast } from 'react-toastify';
import fetchWithTenant from '../../utils/fetchWithTenant';

// ─────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────

const WINDOW_OPTIONS = [
  { value: 5,   label: '±5 minutes' },
  { value: 15,  label: '±15 minutes' },
  { value: 30,  label: '±30 minutes' },
  { value: 60,  label: '±1 hour' },
  { value: 120, label: '±2 hours' },
  { value: 0,   label: 'Custom' },
];

const PRIORITIES = [
  { value: 'LOW',    label: 'Low',    color: '#64748b' },
  { value: 'NORMAL', label: 'Normal', color: '#60a5fa' },
  { value: 'HIGH',   label: 'High',   color: '#f59e0b' },
  { value: 'URGENT', label: 'Urgent', color: '#ef4444' },
];

const SYSTEM_LABELS = {
  FEENICS:   'Feenics ACS',
  LENEL:     'Lenel OnGuard',
  CCURE:     'CCure',
  BRIVO:     'Brivo',
  WAVE:      'Hanwha Wave',
  GENETEC:   'Genetec',
  MILESTONE: 'Milestone',
  OOSTO:     'Oosto OnWatch',
  INTERCOM:  'Intercom',
  CCM_INTERNAL: 'CCM',
};

const STEPS = [
  { id: 1, label: 'Incident Details' },
  { id: 2, label: 'Select Devices' },
  { id: 3, label: 'Review & Create' },
];

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

function toLocalDatetimeValue(date) {
  const d = new Date(date);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDateTime(isoString) {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleString();
}

function systemLabel(s) {
  return SYSTEM_LABELS[s] || s;
}

// ─────────────────────────────────────────────
// STEP 1: INCIDENT DETAILS
// ─────────────────────────────────────────────

function Step1({ data, onChange, errors }) {
  const [customWindow, setCustomWindow] = useState(data.windowMinutes);
  const isCustom = !WINDOW_OPTIONS.slice(0, -1).find(o => o.value === data.windowMinutes);

  return (
    <div className="wizard-step">
      <div className="wizard-step-intro">
        <h3>What happened?</h3>
        <p>Give the investigation a title and tell us when the incident occurred. We'll use this to gather evidence from the right time window.</p>
      </div>

      <div className="wizard-field">
        <label className="wizard-label">Case Title <span className="wizard-req">*</span></label>
        <input
          className={`wizard-input ${errors.title ? 'wizard-err-border' : ''}`}
          type="text"
          placeholder="e.g. Unauthorized after-hours access, Door forced open"
          value={data.title}
          onChange={e => onChange('title', e.target.value)}
          autoFocus
        />
        {errors.title && <span className="wizard-err-msg">{errors.title}</span>}
      </div>

      <div className="wizard-field">
        <label className="wizard-label">Description <span className="wizard-optional">(optional)</span></label>
        <textarea
          className="wizard-textarea"
          placeholder="Brief description of what happened, who reported it, etc."
          rows={3}
          value={data.description}
          onChange={e => onChange('description', e.target.value)}
        />
      </div>

      <div className="wizard-row-2">
        <div className="wizard-field">
          <label className="wizard-label">
            <Calendar size={12} /> Incident Date &amp; Time <span className="wizard-req">*</span>
          </label>
          <input
            className={`wizard-input ${errors.incidentTimestamp ? 'wizard-err-border' : ''}`}
            type="datetime-local"
            value={data.incidentTimestamp}
            onChange={e => onChange('incidentTimestamp', e.target.value)}
          />
          {errors.incidentTimestamp && <span className="wizard-err-msg">{errors.incidentTimestamp}</span>}
          <span className="wizard-hint">When did the incident occur? We'll gather evidence around this time.</span>
        </div>

        <div className="wizard-field">
          <label className="wizard-label"><Clock size={12} /> Evidence Window</label>
          <div className="wizard-window-grid">
            {WINDOW_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                className={`wizard-window-btn ${
                  (!isCustom && data.windowMinutes === opt.value) || (isCustom && opt.value === 0)
                    ? 'active' : ''
                }`}
                onClick={() => {
                  if (opt.value === 0) return; // custom handled below
                  onChange('windowMinutes', opt.value);
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {isCustom && (
            <div className="wizard-custom-window">
              <input
                type="number"
                min={1}
                max={1440}
                className="wizard-input-sm"
                value={customWindow}
                onChange={e => {
                  const v = Number(e.target.value);
                  setCustomWindow(v);
                  onChange('windowMinutes', v);
                }}
              />
              <span className="wizard-hint">minutes on each side</span>
            </div>
          )}
        </div>
      </div>

      <div className="wizard-field">
        <label className="wizard-label">Priority</label>
        <div className="wizard-priority-row">
          {PRIORITIES.map(p => (
            <button
              key={p.value}
              type="button"
              className={`wizard-priority-btn ${data.priority === p.value ? 'active' : ''}`}
              style={data.priority === p.value ? { borderColor: p.color, color: p.color } : {}}
              onClick={() => onChange('priority', p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// STEP 2: DEVICE SELECTION
// ─────────────────────────────────────────────

function DeviceSection({ title, icon: Icon, color, devices, selected, onToggle, onToggleAll, emptyText, loading }) {
  const allSelected = devices.length > 0 && devices.every(d => selected.has(d.key));

  return (
    <div className="wizard-device-section">
      <div className="wizard-device-section-header">
        <div className="wizard-device-section-title">
          <Icon size={14} style={{ color }} />
          <span>{title}</span>
          {devices.length > 0 && (
            <span className="wizard-device-count">{selected.size > 0 ? `${[...selected].filter(k => devices.find(d => d.key === k)).length}/${devices.length}` : devices.length}</span>
          )}
        </div>
        {devices.length > 1 && (
          <button
            type="button"
            className="wizard-select-all"
            onClick={() => onToggleAll(devices, !allSelected)}
          >
            {allSelected ? 'Deselect all' : 'Select all'}
          </button>
        )}
      </div>

      {loading ? (
        <div className="wizard-device-loading"><Loader size={13} className="wizard-spin" /><span>Loading…</span></div>
      ) : devices.length === 0 ? (
        <p className="wizard-device-empty">{emptyText}</p>
      ) : (
        <div className="wizard-device-list">
          {devices.map(device => (
            <button
              key={device.key}
              type="button"
              className={`wizard-device-item ${selected.has(device.key) ? 'selected' : ''}`}
              onClick={() => onToggle(device.key)}
            >
              <div className={`wizard-device-check ${selected.has(device.key) ? 'checked' : ''}`}>
                {selected.has(device.key) && <Check size={10} />}
              </div>
              <div className="wizard-device-info">
                <span className="wizard-device-name">{device.label}</span>
                {device.sub && <span className="wizard-device-sub">{device.sub}</span>}
              </div>
              {device.origin && (
                <span className={`wizard-origin-badge origin-${device.origin}`}>
                  {device.origin === 'discovered' ? 'Discovered' : 'VMS'}
                </span>
              )}
              {device.count != null && (
                <span className="wizard-device-events">{device.count} events</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── ACS section: integration-level selection with optional per-door drill-in ──

function AcsSection({ integrations, bySystem, selected, onToggle, onToggleAll, loading }) {
  const [expanded, setExpanded] = useState(() => new Set());

  const integList = integrations || [];
  const integKeys = integList.map(i => `acs::${i.integrationId || i.id}`);
  const allSelected = integKeys.length > 0 && integKeys.every(k => selected.has(k));

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  return (
    <div className="wizard-device-section">
      <div className="wizard-device-section-header">
        <div className="wizard-device-section-title">
          <DoorOpen size={14} style={{ color: '#5eead4' }} />
          <span>Access Control</span>
          {integList.length > 0 && (
            <span className="wizard-device-count">{integList.length}</span>
          )}
        </div>
        {integList.length > 1 && (
          <button
            type="button"
            className="wizard-select-all"
            onClick={() => onToggleAll(integList.map(i => ({ key: `acs::${i.integrationId || i.id}` })), !allSelected)}
          >
            {allSelected ? 'Deselect all' : 'Select all'}
          </button>
        )}
      </div>

      {loading ? (
        <div className="wizard-device-loading"><Loader size={13} className="wizard-spin" /><span>Loading…</span></div>
      ) : integList.length === 0 ? (
        <p className="wizard-device-empty">No ACS integrations configured. Add Feenics, Brivo, or another access-control integration in Settings.</p>
      ) : (
        <div className="wizard-device-list">
          {integList.map(integ => {
            const integId   = integ.integrationId || integ.id;
            const key       = `acs::${integId}`;
            const isSel     = selected.has(key);
            const doors     = (bySystem || {})[integ.sourceSystem] || [];
            const isExpanded = expanded.has(integId);

            return (
              <div key={integId} className="wizard-acs-integration">
                <button
                  type="button"
                  className={`wizard-device-item ${isSel ? 'selected' : ''}`}
                  onClick={() => onToggle(key)}
                >
                  <div className={`wizard-device-check ${isSel ? 'checked' : ''}`}>
                    {isSel && <Check size={10} />}
                  </div>
                  <div className="wizard-device-info">
                    <span className="wizard-device-name">{integ.displayName}</span>
                    <span className="wizard-device-sub">
                      {doors.length > 0
                        ? `${doors.length} door${doors.length !== 1 ? 's' : ''} with events`
                        : 'All doors from this integration (no events recorded yet)'}
                    </span>
                  </div>
                  {doors.length > 0 && (
                    <span
                      role="button"
                      tabIndex={0}
                      className={`wizard-acs-expand ${isExpanded ? 'expanded' : ''}`}
                      onClick={(e) => { e.stopPropagation(); toggleExpand(integId); }}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); toggleExpand(integId); } }}
                    >
                      {isExpanded ? 'Hide doors' : 'Show all doors'}
                      <ChevronDown size={11} />
                    </span>
                  )}
                </button>

                {isExpanded && doors.length > 0 && (
                  <div className="wizard-acs-doors">
                    {doors.map(d => {
                      const dKey = `acsDoor::${d.sourceDeviceId}`;
                      const dSel = selected.has(dKey);
                      return (
                        <button
                          key={d.sourceDeviceId}
                          type="button"
                          className={`wizard-device-item wizard-device-item-indent ${dSel ? 'selected' : ''}`}
                          onClick={() => onToggle(dKey)}
                        >
                          <div className={`wizard-device-check ${dSel ? 'checked' : ''}`}>
                            {dSel && <Check size={10} />}
                          </div>
                          <div className="wizard-device-info">
                            <span className="wizard-device-name">{d.deviceLabel}</span>
                            <span className="wizard-device-sub wizard-mono">{d.sourceDeviceId}</span>
                          </div>
                          <span className="wizard-device-events">{d.eventCount} events</span>
                        </button>
                      );
                    })}
                    <p className="wizard-acs-doors-hint">
                      Tip: leave doors unselected to include all events from {integ.displayName}; pick specific doors to narrow the search.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Step2({ data, onChange }) {
  const [knownSources, setKnownSources] = useState({ bySystem: {}, sources: [] });
  const [integrations, setIntegrations] = useState({ acs: [], vms: [], intercoms: [], fr: [] });
  const [cameras, setCameras] = useState([]);
  const [camerasUnavailable, setCamerasUnavailable] = useState(false);
  // 868jyxe4c: discovered ONVIF cameras (MdDiscoveredDevice), a 2nd picker group.
  const [discoveredCameras, setDiscoveredCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set(data.deviceSelections?.selected || []));

  useEffect(() => {
    // Four sources in parallel:
    //   - known-sources: distinct devices that have actually produced events
    //   - devices/sources: integrations configured for this tenant
    //   - vms/cameras: live camera list from VMS adapters (per-camera selection)
    //   - md/devices?deviceType=CAMERA: discovered ONVIF cameras (868jyxe4c)
    Promise.all([
      fetchWithTenant('/api/investigations/devices/known-sources')
        .then(r => r.ok ? r.json() : { sources: [], bySystem: {} })
        .catch(() => ({ sources: [], bySystem: {} })),
      fetchWithTenant('/api/investigations/devices/sources')
        .then(r => r.ok ? r.json() : {})
        .catch(() => ({})),
      fetchWithTenant('/api/vms/cameras')
        .then(r => r.ok ? r.json() : null)
        .catch(() => null),
      // 868jyxe4c: discovered cameras share the AnalyticsEvent.cameraId id space
      // (MdDiscoveredDevice.id), so selecting them enables related-analytics.
      fetchWithTenant('/api/md/devices?deviceType=CAMERA')
        .then(r => r.ok ? r.json() : null)
        .catch(() => null),
    ]).then(([known, sources, camsRes, discRes]) => {
      setKnownSources(known || { sources: [], bySystem: {} });
      setIntegrations(sources || {});
      const list = camsRes && Array.isArray(camsRes.cameras) ? camsRes.cameras : [];
      if (list.length > 0) {
        setCameras(list);
      } else {
        setCamerasUnavailable(true);
      }
      // 868jyxe4c: GET /api/md/devices returns { devices: [...] }.
      const discList = discRes && Array.isArray(discRes.devices) ? discRes.devices : [];
      setDiscoveredCameras(discList);
    }).finally(() => setLoading(false));
  }, []);

  const toggle = useCallback((key) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);

  const toggleAll = useCallback((devices, select) => {
    setSelected(prev => {
      const next = new Set(prev);
      devices.forEach(d => select ? next.add(d.key) : next.delete(d.key));
      return next;
    });
  }, []);

  // Sync to parent on every change. The wizard's outbound shape:
  //   acs           = selected ACS integration IDs (broad selection)
  //   acsDoorIds    = optional per-door filter when the user drills in
  //   cameras       = camera IDs (or VMS integration IDs in the fallback case)
  //   fr/intercoms  = integration IDs
  useEffect(() => {
    const acs = [], acsDoorIds = [], camerasSel = [], fr = [], intercoms = [];
    for (const key of selected) {
      const [type, ...rest] = key.split('::');
      const id = rest.join('::');
      if      (type === 'acs')      acs.push(id);
      else if (type === 'acsDoor')  acsDoorIds.push(id);
      else if (type === 'camera')   camerasSel.push(id);
      else if (type === 'fr')       fr.push(id);
      else if (type === 'intercom') intercoms.push(id);
    }
    onChange('deviceSelections', {
      acs, acsDoorIds, cameras: camerasSel, fr, intercoms,
      selected: [...selected],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const bySystem = knownSources.bySystem || {};

  // Cameras: per-camera when /api/vms/cameras returned results; otherwise fall
  // back to selecting the whole VMS integration so the wizard stays usable.
  let cameraDevices;
  let cameraEmptyText;
  if (cameras.length > 0) {
    cameraDevices = cameras.map(c => {
      const id   = c.id || c.cameraId || c.externalId;
      const meta = c.metadata || {};
      const sub  = c.location || c.locationName || c.description ||
                   [meta.manufacturer, meta.model].filter(Boolean).join(' ') ||
                   meta.vmsType || 'Camera';
      return {
        key:    `camera::${id}`,
        label:  c.name || c.cameraName || id,
        sub,
        origin: 'vms',
      };
    });
    cameraEmptyText = 'No cameras available.';
  } else {
    cameraDevices = (integrations.vms || []).map(v => ({
      key:   `camera::${v.integrationId || v.id}`,
      label: v.displayName,
      sub:   camerasUnavailable
        ? 'All cameras from this VMS (camera list unavailable)'
        : 'All cameras from this VMS',
      origin: 'vms',
    }));
    cameraEmptyText = 'No VMS integrations configured.';
  }

  // 868jyxe4c: discovered ONVIF cameras (MdDiscoveredDevice). Exclude
  // integration-sourced rows: those duplicate the VMS list above, and their
  // analytics is keyed by the VMS handle rather than this cuid. The stored id
  // MUST be d.id, because that is the value AnalyticsEvent.cameraId holds for
  // ONVIF tenants, so a selection here joins to related analytics.
  const discoveredCameraDevices = (discoveredCameras || [])
    .filter(d => d.source !== 'INTEGRATION')
    .map(d => ({
      key:    `camera::${d.id}`,
      label:  d.name || d.hostname || d.ipAddress || d.id,
      sub:    d.ipAddress || 'Discovered camera',
      origin: 'discovered',
    }));

  const frDevices = (integrations.fr || []).map(f => ({
    key:   `fr::${f.integrationId || f.id}`,
    label: f.displayName,
    sub:   'All recognition events',
  }));

  const intercomDevices = (integrations.intercoms || []).map(i => ({
    key:   `intercom::${i.deviceId || i.id}`,
    label: i.displayName,
    sub:   'Call logs',
  }));

  const acsList = integrations.acs || [];
  const noDevices = !loading
    && acsList.length === 0
    && cameraDevices.length === 0
    && discoveredCameraDevices.length === 0
    && frDevices.length === 0
    && intercomDevices.length === 0;

  return (
    <div className="wizard-step">
      <div className="wizard-step-intro">
        <h3>Where do you want to look?</h3>
        <p>Select the devices to pull evidence from. We'll gather access events, footage clips, facial recognition matches, and intercom logs from the time window you set.</p>
      </div>

      {selected.size > 0 && (
        <div className="wizard-selection-summary">
          <Check size={13} />
          <span>{selected.size} item{selected.size !== 1 ? 's' : ''} selected</span>
        </div>
      )}

      <AcsSection
        integrations={acsList}
        bySystem={bySystem}
        selected={selected}
        onToggle={toggle}
        onToggleAll={toggleAll}
        loading={loading}
      />

      <DeviceSection
        title="VMS cameras"
        icon={Video}
        color="#60a5fa"
        devices={cameraDevices}
        selected={selected}
        onToggle={toggle}
        onToggleAll={toggleAll}
        emptyText={cameraEmptyText}
        loading={loading}
      />

      {/* 868jyxe4c: discovered ONVIF cameras; omit the group quietly when none. */}
      {discoveredCameraDevices.length > 0 && (
        <DeviceSection
          title="Discovered cameras"
          icon={Video}
          color="#34d399"
          devices={discoveredCameraDevices}
          selected={selected}
          onToggle={toggle}
          onToggleAll={toggleAll}
          emptyText="No discovered cameras."
          loading={loading}
        />
      )}

      <DeviceSection
        title="Facial Recognition"
        icon={Activity}
        color="#a855f7"
        devices={frDevices}
        selected={selected}
        onToggle={toggle}
        onToggleAll={toggleAll}
        emptyText="No FR integrations configured."
        loading={loading}
      />

      <DeviceSection
        title="Intercoms"
        icon={Phone}
        color="#fb923c"
        devices={intercomDevices}
        selected={selected}
        onToggle={toggle}
        onToggleAll={toggleAll}
        emptyText="No intercom integrations configured."
        loading={loading}
      />

      {noDevices && (
        <div className="wizard-no-devices">
          <AlertCircle size={20} />
          <div>
            <strong>No devices found.</strong>
            <p>Ensure integrations are configured in Settings. ACS events will appear here once doors start generating events.</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// STEP 3: REVIEW
// ─────────────────────────────────────────────

function Step3({ data }) {
  const priority = PRIORITIES.find(p => p.value === data.priority) || PRIORITIES[1];
  const windowOpt = WINDOW_OPTIONS.find(o => o.value === data.windowMinutes);
  const windowLabel = windowOpt && windowOpt.value !== 0 ? windowOpt.label : `±${data.windowMinutes} minutes`;

  const ds = data.deviceSelections || {};
  const totalDevices = (ds.acs?.length || 0) + (ds.cameras?.length || 0) + (ds.fr?.length || 0) + (ds.intercoms?.length || 0);

  return (
    <div className="wizard-step">
      <div className="wizard-step-intro">
        <h3>Ready to gather evidence</h3>
        <p>We'll start pulling evidence from all selected sources immediately after the case is created.</p>
      </div>

      <div className="wizard-review-card">
        <div className="wizard-review-row">
          <span className="wizard-review-label">Case Title</span>
          <span className="wizard-review-value">{data.title}</span>
        </div>
        {data.description && (
          <div className="wizard-review-row">
            <span className="wizard-review-label">Description</span>
            <span className="wizard-review-value wizard-review-desc">{data.description}</span>
          </div>
        )}
        <div className="wizard-review-row">
          <span className="wizard-review-label">Incident Time</span>
          <span className="wizard-review-value">{formatDateTime(data.incidentTimestamp)}</span>
        </div>
        <div className="wizard-review-row">
          <span className="wizard-review-label">Evidence Window</span>
          <span className="wizard-review-value">{windowLabel}</span>
        </div>
        <div className="wizard-review-row">
          <span className="wizard-review-label">Priority</span>
          <span className="wizard-review-value" style={{ color: priority.color, fontWeight: 600 }}>{priority.label}</span>
        </div>
      </div>

      <div className="wizard-review-devices">
        <div className="wizard-review-devices-header">
          <Shield size={14} />
          <span>Evidence Sources ({totalDevices} device{totalDevices !== 1 ? 's' : ''})</span>
        </div>
        {totalDevices === 0 ? (
          <div className="wizard-review-no-devices">
            <AlertCircle size={14} />
            <span>No devices selected — case will be created with no evidence gathered. You can add devices later.</span>
          </div>
        ) : (
          <div className="wizard-review-device-rows">
            {(ds.acs?.length > 0) && (
              <div className="wizard-review-device-type">
                <DoorOpen size={13} style={{ color: '#5eead4' }} />
                <span>
                  <strong>{ds.acs.length}</strong> ACS integration{ds.acs.length !== 1 ? 's' : ''}
                  {ds.acsDoorIds?.length > 0
                    ? ` (${ds.acsDoorIds.length} specific door${ds.acsDoorIds.length !== 1 ? 's' : ''})`
                    : ' (all doors)'}
                </span>
              </div>
            )}
            {(ds.cameras?.length > 0) && (
              <div className="wizard-review-device-type">
                <Video size={13} style={{ color: '#60a5fa' }} />
                <span><strong>{ds.cameras.length}</strong> camera{ds.cameras.length !== 1 ? 's' : ''} (footage clips)</span>
              </div>
            )}
            {(ds.fr?.length > 0) && (
              <div className="wizard-review-device-type">
                <Activity size={13} style={{ color: '#a855f7' }} />
                <span><strong>{ds.fr.length}</strong> FR engine{ds.fr.length !== 1 ? 's' : ''} (face matches)</span>
              </div>
            )}
            {(ds.intercoms?.length > 0) && (
              <div className="wizard-review-device-type">
                <Phone size={13} style={{ color: '#fb923c' }} />
                <span><strong>{ds.intercoms.length}</strong> intercom{ds.intercoms.length !== 1 ? 's' : ''} (call logs)</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="wizard-review-note">
        <Clock size={13} />
        <span>Evidence gathering runs in the background. The case will open immediately — you can view incoming evidence as it's collected.</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN WIZARD
// ─────────────────────────────────────────────

export default function CaseCreationWizard({ isOpen, onClose, onCreated, prefillEventId = null, prefillTimestamp = null }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(() => ({
    title:             '',
    description:       '',
    incidentTimestamp: toLocalDatetimeValue(prefillTimestamp || new Date()),
    windowMinutes:     15,
    priority:          'NORMAL',
    deviceSelections:  { acs: [], cameras: [], fr: [], intercoms: [], selected: [] },
  }));
  const [errors, setErrors] = useState({});

  // Reset on open
  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setErrors({});
    setSaving(false);
    setData({
      title:             '',
      description:       '',
      incidentTimestamp: toLocalDatetimeValue(prefillTimestamp || new Date()),
      windowMinutes:     15,
      priority:          'NORMAL',
      deviceSelections:  { acs: [], cameras: [], fr: [], intercoms: [], selected: [] },
    });
  }, [isOpen, prefillTimestamp]);

  const onChange = useCallback((field, value) => {
    setData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => { const e = { ...prev }; delete e[field]; return e; });
  }, [errors]);

  const validateStep = (s) => {
    const e = {};
    if (s === 1) {
      if (!data.title.trim())          e.title = 'Case title is required';
      if (!data.incidentTimestamp)     e.incidentTimestamp = 'Incident time is required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (!validateStep(step)) return;
    setStep(s => s + 1);
  };

  const handleBack = () => {
    setErrors({});
    setStep(s => s - 1);
  };

  const handleCreate = async () => {
    if (!validateStep(step)) return;
    setSaving(true);
    try {
      const body = {
        title:             data.title.trim(),
        description:       data.description?.trim() || undefined,
        priority:          data.priority,
        incidentTimestamp: data.incidentTimestamp ? new Date(data.incidentTimestamp).toISOString() : undefined,
        windowMinutes:     data.windowMinutes,
        deviceSelections:  {
          acs:        data.deviceSelections.acs || [],
          acsDoorIds: data.deviceSelections.acsDoorIds || [],
          cameras:    data.deviceSelections.cameras || [],
          fr:         data.deviceSelections.fr || [],
          intercoms:  data.deviceSelections.intercoms || [],
        },
        eventId: prefillEventId || undefined,
      };

      const r = await fetchWithTenant('/api/investigations/cases', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to create case');
      }

      const created = await r.json();
      toast.success('Case created — gathering evidence now');
      onCreated?.(created);
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to create case');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const isLastStep = step === STEPS.length;

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && !saving && onClose()}>
      <div className="wizard-modal">

        {/* Header */}
        <div className="wizard-header">
          <div className="wizard-header-title">
            <Shield size={18} />
            <span>New Investigation</span>
          </div>
          <button className="wizard-close" onClick={onClose} disabled={saving}><X size={16} /></button>
        </div>

        {/* Progress */}
        <div className="wizard-progress">
          {STEPS.map((s, i) => (
            <div key={s.id} className={`wizard-progress-step ${step === s.id ? 'active' : step > s.id ? 'done' : ''}`}>
              <div className="wizard-progress-dot">
                {step > s.id ? <Check size={10} /> : s.id}
              </div>
              <span className="wizard-progress-label">{s.label}</span>
              {i < STEPS.length - 1 && <div className="wizard-progress-line" />}
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="wizard-body">
          {step === 1 && <Step1 data={data} onChange={onChange} errors={errors} />}
          {step === 2 && <Step2 data={data} onChange={onChange} />}
          {step === 3 && <Step3 data={data} />}
        </div>

        {/* Footer */}
        <div className="wizard-footer">
          <button className="btn-secondary" onClick={step === 1 ? onClose : handleBack} disabled={saving}>
            {step === 1 ? 'Cancel' : <><ChevronLeft size={14} /> Back</>}
          </button>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <span className="wizard-step-indicator">Step {step} of {STEPS.length}</span>
            {isLastStep ? (
              <button className="btn-primary" onClick={handleCreate} disabled={saving}>
                {saving ? <><Loader size={13} className="wizard-spin" /> Creating…</> : <><Shield size={13} /> Create &amp; Gather Evidence</>}
              </button>
            ) : (
              <button className="btn-primary" onClick={handleNext}>
                Next <ChevronRight size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
